import {
  occurrenceCalendarRequestScope,
  assertOccurrenceResponseScope,
  ACTIVITY_PATHS,
  buildActivityActionPath,
  createActivitiesClient,
  readActivityListResponse,
  readActivityRecordResponse,
  readActivityCreationResponse,
  readActivityConfirmationResponse,
  readActivityDeletionResponse,
  readActivityVenuesResponse,
  readActivityIdentifier,
  nextActivitySession,
  readActivityVenueQuery,
} from '@liquid-spirit/api/domains/activities';
import { readUploadS3UrlResponse, UPLOAD_PATHS } from '@liquid-spirit/api/domains/uploads';
import apiClient from './apiClient';
import { requestSessionParticipation, requestSessionFacilitator } from './SessionService';
import { createCacheAdapter } from './apiCacheAdapter';
import { invalidateActivityCaches } from './frontendCacheInvalidation';
import { dataFlowLog } from '../utils/dataFlowLogger';

const ACTIVITIES_BASE = ACTIVITY_PATHS.collection;

const buildQueryString = (params = {}) => {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null);
  if (!entries.length) return '';
  const search = new URLSearchParams(entries).toString();
  return search ? `?${search}` : '';
};

const computeCacheKey = (url, overrideKey) => {
  if (overrideKey) return overrideKey;
  if (url === ACTIVITIES_BASE) return 'activities:list';
  if (url.startsWith(`${ACTIVITIES_BASE}/`)) {
    return `activities:${url.slice(ACTIVITIES_BASE.length + 1)}`;
  }
  return `activities:${url}`;
};

const makeRequest = async (path, method, body = null, config = {}) => {
  const {
    params,
    headers = {},
    signal,
    token,
    returnJson = false,
    cacheKey,
    useCache = method === 'GET',
    persist,
    storageKey,
    staleMs,
    revalidate = true,
  } = config;

  let url = path;
  if (params) {
    url += buildQueryString(params);
  }

  if (method === 'GET' && useCache) {
    const key = computeCacheKey(url, cacheKey);
    const cacheAdapter = createCacheAdapter({
      key,
      persist: typeof persist === 'boolean' ? persist : url === ACTIVITIES_BASE,
      storageKey: storageKey ?? (url === ACTIVITIES_BASE ? 'activitiesCache' : undefined),
      staleMs,
      revalidate,
      credentialScoped: url.startsWith(ACTIVITIES_BASE),
      validateResponse: path === ACTIVITIES_BASE || path.startsWith(`${ACTIVITIES_BASE}/user/`)
        ? readActivityListResponse : readActivityRecordResponse,
    });

    return apiClient.request(path, {
      method: 'GET',
      params,
      headers,
      signal,
      token,
      contract: 'payload',
      cacheAdapter,
    });
  }

  if (returnJson) {
    return apiClient.request(path, {
      method,
      body,
      params,
      headers,
      signal,
      token,
      contract: 'payload',
    });
  }

  await apiClient.request(path, {
    method,
    body,
    params,
    headers,
    signal,
    token,
    contract: 'payload',
  });
  return true;
};

// Activity paths live in @liquid-spirit/api; the caching machinery above stays
// here. Participation/facilitation select an active session and use SessionService.
const activities = createActivitiesClient({
  get: (path, options = {}) => makeRequest(path, 'GET', null, options),
  post: (path, body, options = {}) => makeRequest(path, 'POST', body, options),
  patch: (path, body, options = {}) => makeRequest(path, 'PATCH', body, options),
  delete: (path, options = {}) => makeRequest(path, 'DELETE', null, options),
});

export const fetchActivities = (options = {}) =>
  activities.list({
    persist: true,
    storageKey: 'activitiesCache',
    ...options,
  }).then(readActivityListResponse);

export const fetchUserActivities = (userId, options = {}) =>
  activities.listForUser(userId, options).then(result => readActivityListResponse(result, 'user activities'));

export const fetchActivityDetails = (activityId, options = {}) => {
  dataFlowLog('activity-service.fetch-details', {
    activityId,
    params: options?.params || null,
    useCache: options?.useCache !== false,
    cacheKey: options?.cacheKey || null,
    forceRefresh: Boolean(options?.returnJson),
  });
  if (Object.hasOwn(options.params || {}, 'displayTimeZone')) {
    const scope = options.calendarScope || occurrenceCalendarRequestScope(options.params);
    return activities.getById(activityId, {...options, useCache: false, returnJson: true})
      .then(payload => readActivityRecordResponse(assertOccurrenceResponseScope(payload, scope)));
  }
  return activities.getById(activityId, options).then(readActivityRecordResponse);
};

export const getAvailableVenuesForActivity = (activityId, options = {}) => {
  readActivityIdentifier(activityId);
  readActivityVenueQuery(options.params);
  return makeRequest(buildActivityActionPath(activityId, 'available-venues'), 'GET', null, {
    useCache: false,
    ...options,
  }).then(readActivityVenuesResponse);
};

export const getActivityVenues = (activityId, options = {}) => {
  readActivityIdentifier(activityId);
  return makeRequest(buildActivityActionPath(activityId, 'venues'), 'GET', null, {
    useCache: false,
    ...options,
  }).then(readActivityVenuesResponse);
};

const fetchCategory = async (names, options) => {
  const normalize = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const accepted = new Set(names.map(normalize));
  const rows = await fetchActivities(options);
  return rows.filter(activity => accepted.has(normalize(activity.activityType?.name)))
    .map(activity => {
      const session = nextActivitySession(activity, new Date(), options?.params?.displayTimeZone ? {timeZone: options.params.displayTimeZone} : undefined);
      const people = key => (session?.[key] || []).map(entity => entity.details || entity.refId)
        .filter(person => person && typeof person === 'object');
      return { ...activity, ...activity.groupDetails, participants: people('participants'), facilitators: people('facilitators') };
    });
};

export const fetchChildrensClasses = (options = {}) => fetchCategory(['Children\'s Class', 'Children\'s Classes'], options);
export const fetchDevotionals = (options = {}) => fetchCategory(['Devotional', 'Devotional Gathering', 'Devotional Meeting'], options);
export const fetchStudyCircles = (options = {}) => fetchCategory(['Study Circle', 'Study Circles'], options);
export const fetchJuniorYouth = (options = {}) => fetchCategory(['Junior Youth', 'Junior Youth Group', 'Junior Youth Program'], options);

const requestForActivity = async (activityId, options, request) => {
  const activity = await fetchActivityDetails(activityId, { ...options, useCache: false, returnJson: true });
  const session = nextActivitySession(activity, new Date(), options?.params?.displayTimeZone ? {timeZone: options.params.displayTimeZone} : undefined);
  if (!session) throw new Error('This activity has no upcoming session to join.');
  return request(session._id, options);
};
export const requestActivityParticipation = (activityId, options = {}) => requestForActivity(activityId, options, requestSessionParticipation);
export const requestActivityFacilitation = (activityId, options = {}) => requestForActivity(activityId, options, requestSessionFacilitator);

export const createActivity = (payload, options = {}) => {
  if (import.meta.env?.DEV) {
    // Helpful diagnostic to inspect the request body before it is sent.
    console.info('[ActivityService] createActivity request', {
      url: ACTIVITY_PATHS.create,
      payload,
      options,
    });
  }

  return activities.create(payload, {
    ...options,
    returnJson: true,
  }).then((result) => {
    readActivityCreationResponse(result);
    invalidateActivityCaches({ activityId: result.activity._id });
    return result;
  });
};

export const updateActivity = (activityId, updates, options = {}) =>
  activities.update(activityId, updates, {
    ...options,
    returnJson: true,
  }).then((result) => {
    readActivityRecordResponse(result, 'activity update');
    invalidateActivityCaches({ activityId });
    return result;
  });

export const deleteActivity = (activityId, options = {}) =>
  activities.remove(activityId, {
    ...options,
    returnJson: true,
  }).then((result) => {
    readActivityDeletionResponse(result);
    invalidateActivityCaches({ activityId });
    return result;
  });

export const confirmActivity = (activityId, options = {}) =>
  activities.confirm(activityId, {
    useCache: false,
    ...options,
    returnJson: true,
  }).then((result) => {
    readActivityConfirmationResponse(result);
    invalidateActivityCaches({ activityId });
    return result;
  });

export const cancelActivity = (activityId, options = {}) =>
  activities.cancel(activityId, {
    ...options,
    returnJson: true,
  }).then((result) => {
    readActivityRecordResponse(result, 'activity cancellation');
    invalidateActivityCaches({ activityId });
    return result;
  });

export const uploadActivityDetailsImage = async (file, activityId) => {
  try {
    readActivityIdentifier(activityId);
    const extensionMatch = file.name.match(/\.[0-9a-z]+$/i);
    const extension = extensionMatch ? extensionMatch[0] : '';
    const fileName = `activity-${activityId}-${Date.now()}${extension}`;

    const { url: signedUrl } = readUploadS3UrlResponse(await makeRequest(UPLOAD_PATHS.s3Url, 'GET', null, {
      params: {
        fileName,
        fileType: file.type,
      },
      useCache: false,
      returnJson: true,
    }));

    const uploadResponse = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload file to S3: ${uploadResponse.statusText}`);
    }

    const imgUrl = signedUrl.split('?')[0];

    await updateActivity(activityId, { imageUrl: imgUrl });

    return { stage: 'completed', message: 'Image upload and activity update complete!', imgUrl };
  } catch (error) {
    console.error('Error uploading activity image:', error);
    throw { stage: 'error', message: error.message || 'An unexpected error occurred during upload.' };
  }
};
